import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "rhombus-react";

import {
	SUBMIT_AD,
	APPROVE_AD,
	RECALL_AD,
	REJECT_AD,
	VIEW_APPROVAL_HISTORY
} from '../grid/cells/CreativeActionLists/ApprovalActions';
import { openModal } from 'reducers/Modal';
import { MODAL_TYPES } from 'utils/constants';
import { isImposiumCreative, isVideoBuilderCreative } from "utils/common";

import { isInvalidDVASTState, isTranscodingError, shouldDisplayAction } from 'utils/CreativeActions';
import { selectedCreativeData } from "selectors/Selected";

function getSubmittableItems(user, selectedItems) {
	return selectedItems.filter((creativeInfo) => {
		return !isVideoBuilderCreative(creativeInfo) && shouldDisplayAction.submit(user, creativeInfo);
	});
}

function getApprovableItems(user, selectedItems) {
	return selectedItems.filter((creativeInfo) => {
		return !isVideoBuilderCreative(creativeInfo) && shouldDisplayAction.approve(user, creativeInfo);
	});
}

function getRecallableItems(user, selectedItems) {
	return selectedItems.filter((creativeInfo) => {
		return !isVideoBuilderCreative(creativeInfo) && shouldDisplayAction.recall(user, creativeInfo);
	});
}

function getRejectableItems(user, selectedItems) {
	return selectedItems.filter((creativeInfo) => {
		return !isVideoBuilderCreative(creativeInfo) && shouldDisplayAction.reject(user, creativeInfo);
	});
}

function getApprovalHistoryItems(user, selectedItems) {
	return selectedItems.filter(creativeInfo => !isVideoBuilderCreative(creativeInfo));
}

export function BulkApprovalButton() {
	const dispatch = useDispatch();
	let {
		selectedItems,
		user,
		libraryId,
	} = useSelector(state=> ({
		selectedItems: selectedCreativeData(state),
		user: state.you,
		libraryId: state.libraryProperties.id,
	}));

	const submittableItems = useMemo(() => getSubmittableItems(user, selectedItems), [user, selectedItems]);
	const approvableItems = useMemo(() => getApprovableItems(user, selectedItems), [user, selectedItems]);
	const recallableItems = useMemo(() => getRecallableItems(user, selectedItems), [user, selectedItems]);
	const rejectableItems = useMemo(() => getRejectableItems(user, selectedItems), [user, selectedItems]);
	const approvalHistoryItems = useMemo(() => getApprovalHistoryItems(user, selectedItems), [user, selectedItems]);
	const isApprovalWorkflowDisabled = selectedItems.every(item => (
		isInvalidDVASTState(item) && !(isImposiumCreative(item) && isTranscodingError(item.approval))
	));

	let splitActions = [];


	/*todo:
	* un-disable each item as it gets implemented
	*/

	if (submittableItems.length) {
		splitActions.push(Object.assign({}, SUBMIT_AD, {
			name: `Submit to QA (${submittableItems.length})`,
			icon: 'fas fa-arrow-circle-right',
			className:"submit-to-qa-action",
			onClick: () => dispatch(openModal(MODAL_TYPES.SUBMIT_CREATIVES, {
				creatives: submittableItems.map(creative => ({
					id: creative.creativeId,
					name: creative.creativeName,
					approval: creative.approval,
					adType: creative.adType,
				})),
				libraryId,
			})),
		}));
	}

	if (approvableItems.length) {
		splitActions.push(Object.assign({}, APPROVE_AD, {
			name: `Approve (${approvableItems.length})`,
			icon: 'fas fa-check-circle',
			className:"approve-action",
			onClick: () => dispatch(openModal(MODAL_TYPES.APPROVE_CREATIVES, {
				creatives: approvableItems.map(creative => ({
					id: creative.creativeId,
					name: creative.creativeName,
					approval: creative.approval,
					adType: creative.adType,
				})),
				libraryId,
			})),
		}));
	}

	if (recallableItems.length) {
		splitActions.push(Object.assign({}, RECALL_AD, {
			name: `Recall (${recallableItems.length})`,
			icon: 'fas fa-arrow-circle-left',
			className:"recall-action",
			onClick: () => dispatch(openModal(MODAL_TYPES.RECALL_CREATIVES, {
				creatives: recallableItems.map(creative => ({
					id: creative.creativeId,
					name: creative.creativeName,
					approval: creative.approval,
				})),
				libraryId,
			})),
		}));
	}

	if (rejectableItems.length) {
		splitActions.push(Object.assign({}, REJECT_AD, {
			name: `Reject (${rejectableItems.length})`,
			icon: 'fas fa-times-circle',
			className:"reject-action",
			onClick: () => dispatch(openModal(MODAL_TYPES.REJECT_CREATIVES, {
				creatives: rejectableItems.map(creative => ({
					id: creative.creativeId,
					name: creative.creativeName,
					approval: creative.approval,
				})),
				libraryId,
			})),
		}));
	}

	if(approvalHistoryItems.length){
		if (splitActions.length) {
			splitActions.push({ divider: true });
		}

		splitActions.push(Object.assign({}, VIEW_APPROVAL_HISTORY, {
			name: `View Approval History (${approvalHistoryItems.length})`,
			icon: 'fas fa-history',
			className:"approval-history-action",
			onClick: () => dispatch(openModal(MODAL_TYPES.APPROVAL_HISTORY, {
				creatives: approvalHistoryItems.map(creative => ({
					id: creative.creativeId,
					name: creative.creativeName,
					approval: creative.approval,
				})),
				libraryId,
			})),
		}));
	}

	return (
		<Button
			style="block"
			icon="fas fa-badge-check"
			theme="blue"
			isDropDown
			addClass="true"
			disabled={isApprovalWorkflowDisabled || !splitActions.length}
			splitActions={splitActions}
			splitOptions={{ footerHeight: 117000, menuClass: 'bulk-approval-menu' }}
			splitValue="bulk-approval"
		>
			Approval Workflow
		</Button>
	);
}

BulkApprovalButton.displayName = "BulkApprovalButton";
